import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Container, Divider, Grid, Header, Icon, Table } from 'semantic-ui-react'

import { AppState } from '../../redux/store'
import { AbbreviatedProduct, AbbreviatedProductContainer } from '../../consts'
import CurrentQueue from '../BulkEditor/CurrentQueue'
import LookupProduct from '../LookupProduct/LookupProduct'
import StepOneZipUploader from '../BatchProductCreator/Steps/StepOneZipUploader'
import { postAProduct } from '../../redux/actions/products'
import BulkEditResults from '../BulkEditor/BulkEditResults'
import CSVReader from './CSVReader'
import CSVDownloader from './CSVDownloader'
import Row from './Row'

const ClumpEditor = () => {
  const zipFileData = useSelector((state: AppState) => state.batchProductCreator.zipFileResult)
  const dispatch = useDispatch()
  const [step, setStep] = useState<number>(0)
  const [productsQueue, setProductsQueue] = useState<AbbreviatedProductContainer>({})

  const [uploadedData, setUploadedData] = useState([])
  const [submitted, setSubmitted] = useState(false)

  const addProductToProductsQueue = (abbrevProduct: AbbreviatedProduct | AbbreviatedProduct[]) => {
    const newQueue = { ...productsQueue }
    if (abbrevProduct instanceof Array) {
      abbrevProduct.forEach((ap) => {
        newQueue[ap.id] = ap
      })
      setProductsQueue(newQueue)
    } else {
      newQueue[abbrevProduct.id] = abbrevProduct
      setProductsQueue(newQueue)
    }
  }

  useEffect(() => {
    if (zipFileData) {
      setStep(2)
    }
  }, [zipFileData])

  const removeProductFromProductsQueue = (id: number) => {
    const newQueue = { ...productsQueue }
    delete newQueue[id]
    setProductsQueue(newQueue)
  }

  const queueSize = useMemo(() => Object.keys(productsQueue).length, [productsQueue])

  const nextStepBtn = () => {
    // clear out products queue & change step
    const changeStepWithBtn = (n: number) => {
      setProductsQueue({})
      setStep(n)
    }

    const toStepThree = (
      <Button primary onClick={() => changeStepWithBtn(2)}>
        Ready, no photo changes
      </Button>
    )

    if (step === 0) {
      return (
        <>
          {toStepThree}
          <Button secondary onClick={() => changeStepWithBtn(1)}>
            Ready, do have photo changes
          </Button>
          <CSVDownloader productIds={Object.keys(productsQueue)} />
        </>
      )
    } else if (step === 1) {
      if (zipFileData) {
        return (
          <Button primary onClick={() => changeStepWithBtn(2)}>
            Next
          </Button>
        )
      } else {
        return toStepThree
      }
    }
  }

  const clearCartBtn = () => {
    if (queueSize > 0 && step === 0) {
      return (
        <Button color="yellow" onClick={() => setProductsQueue({})}>
          Clear queue
        </Button>
      )
    }
    return null
  }

  const gridWithCart = (child: React.ReactNode) => (
    <Grid columns={2} divided>
      <Grid.Column width={12}>{child}</Grid.Column>
      <Grid.Column width={4}>
        <Header as="h2">
          Current Queue
          <Header.Subheader className="bulk-editor-queue-count">
            {queueSize} Product(s) In Queue
          </Header.Subheader>
        </Header>
        {clearCartBtn()}
        <CurrentQueue
          cart={productsQueue}
          removeFromCart={(id: number) => removeProductFromProductsQueue(id)}
        />
      </Grid.Column>
    </Grid>
  )

  function getStepOneBuildQueue() {
    return gridWithCart(
      <div style={{ margin: '0 auto' }}>
        <LookupProduct
          addProductToBulkEditProductsQueue={(
            abbrevProduct: AbbreviatedProduct | AbbreviatedProduct[]
          ) => addProductToProductsQueue(abbrevProduct)}
          bulkEditProductsQueue={productsQueue}
          mode="bulkEdit"
          removeProductFromBulkEditProductsQueue={(id: number) =>
            removeProductFromProductsQueue(id)
          }
          onModalActiveChange={() => null}
        />
      </div>
    )
  }

  const buildFormData = (row: Row) => {
    const photoUrls: string[] = []
    if (zipFileData) {
      const { photo } = row
      photo.split(';').forEach((rawUrl) => {
        const url = rawUrl.trim()
        if (zipFileData[url]) {
          photoUrls.push(zipFileData[url])
        }
      })
    }
    const formData = new FormData()
    const newRow = { ...row, photo: photoUrls.join(';') }
    Object.entries(newRow).forEach(([key, value]) => {
      if (value && key === 'id') {
        formData.append('id', value)
      } else if (value && key === 'name') {
        formData.append('name', value)
      } else if (value && key === 'brand') {
        formData.append('brand', value)
      } else if (value && key === 'description') {
        formData.append('description', value)
      } else if (value && key === 'photo') {
        value.split(';').forEach((url: string) => {
          formData.append('image_urls[]', url)
        })
      } else if (value && key === 'product_type') {
        formData.append('kind', value.toLowerCase())
      } else if (value && key === 'lineage') {
        formData.append('category', value.toLowerCase())
      } else if (value && key === 'brand_subtype') {
        formData.append('kind_subtype', value)
      } else if (value && key === 'product_subtype') {
        formData.append('root_subtype', value)
      } else if (value && key === 'dosage') {
        formData.append('dosage', value)
      } else if (value && key === 'amount') {
        formData.append('amount', value)
      }
    })
    return formData
  }

  const editProductFromRow = (row: Row) => {
    const formData = buildFormData(row)
    // @ts-ignore
    productsQueue[parseInt(row.id)] = {
      ...Object.fromEntries(formData),
    }
    dispatch(
      postAProduct({
        formData: buildFormData(row),
        mode: 'edit',
        isBatchLookup: false,
        isBulkEdit: true,
      })
    )
  }

  const getCsvPreview = () => {
    return (
      <div>
        <Button
          fluid
          primary
          onClick={() => {
            setSubmitted(true)
            uploadedData.filter((e: Row) => e.id).forEach((e: Row) => editProductFromRow(e))
          }}
        >
          Submit Edits
        </Button>
        <Table>
          <thead>
            <tr>
              <td>ID</td>
              <td>Name</td>
              <td>Brand</td>
              <td>Description</td>
              <td>Photo</td>
              <td>Lineage</td>
              <td>Product Type</td>
              <td>Product Subtype</td>
              <td>Brand Subtype</td>
              <td>Dosage</td>
              <td>Amount</td>
            </tr>
          </thead>
          <tbody>
            {uploadedData.map((e: Row) => (
              <tr key={e.id}>
                <td>{e.id}</td>
                <td>{e.name}</td>
                <td>{e.brand}</td>
                <td>{e.description}</td>
                <td>{e.photo}</td>
                <td>{e.lineage}</td>
                <td>{e.product_type}</td>
                <td>{e.product_subtype}</td>
                <td>{e.brand_subtype}</td>
                <td>{e.dosage}</td>
                <td>{e.amount}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    )
  }

  const stepThree = () => {
    return (
      <div>
        <CSVReader setUploadedData={setUploadedData} setSubmitted={setSubmitted} />
        {!submitted && uploadedData && getCsvPreview()}
        {submitted && <BulkEditResults productsQueue={productsQueue} />}
      </div>
    )
  }
  const getBody = () => {
    switch (step) {
      case 0:
        return getStepOneBuildQueue()
      case 1:
        return <StepOneZipUploader />
      case 2:
        return stepThree()
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <Container textAlign="center">
        <Header id="bulk-editor-header" as="h1" textAlign="center">
          <Icon name="pencil square" circular style={{ display: 'inline-block' }} />
          <Header.Content style={{ display: 'inline-block' }}>Clump Editor</Header.Content>
        </Header>
        {nextStepBtn()}
        <Button
          color="red"
          onClick={() => {
            // @ts-ignore
            window.location.reload()
          }}
        >
          Reset
        </Button>
      </Container>

      <Divider />

      {getBody()}
    </div>
  )
}

export default ClumpEditor
